import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// Types matching server responses
interface ClaimResponse {
  player_id: string;
  api_key: string;
  organization_id: string;
}

interface ConfigVideo {
  id: string;
  filename: string;
  download_url: string;
}

interface ConfigPlaylistItem {
  item_type: string; // "video" | "web_url" | "image"
  video: ConfigVideo | null;
  url: string | null;
  duration_seconds: number | null;
}

interface ZoneTriggerConfig {
  zone_id: string;
  video: ConfigVideo;
}

interface DemographicVideoConfig {
  video: ConfigVideo;
  target_gender: string | null;
  target_age_min: number | null;
  target_age_max: number | null;
}

interface MediaPlayerConfig {
  player_id: string;
  camera_id: string | null;
  playlist: ConfigPlaylistItem[];
  zone_triggers: ZoneTriggerConfig[];
  demographic_videos: DemographicVideoConfig[];
}

interface PendingTrigger {
  id: number;
  zone_id: string | null;
  trigger_type: string;
  video_id: string | null;
  triggered_at: string;
}

interface Credentials {
  playerId: string;
  apiKey: string;
  serverUrl: string;
}

const STORAGE_KEY = 'pw-tv-credentials';

function loadCredentials(): Credentials | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveCredentials(creds: Credentials) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(creds));
}

function clearCredentials() {
  localStorage.removeItem(STORAGE_KEY);
}

// Fetch helper with API key
async function apiFetch(serverUrl: string, path: string, apiKey: string, options?: RequestInit) {
  const res = await fetch(`${serverUrl}${path}`, {
    ...options,
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res;
}

// ─── Setup Screen ────────────────────────────────────────────

function SetupScreen({ onConnected }: { onConnected: (creds: Credentials) => void }) {
  const [searchParams] = useSearchParams();
  const [serverUrl, setServerUrl] = useState(searchParams.get('server') || '');
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const autoConnectRef = useRef(false);

  const connect = useCallback(async () => {
    if (!serverUrl || !code) return;
    setConnecting(true);
    setError(null);

    try {
      const res = await fetch(`${serverUrl}/media-players/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_code: code }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const data: ClaimResponse = await res.json();
      const creds: Credentials = {
        playerId: data.player_id,
        apiKey: data.api_key,
        serverUrl,
      };
      saveCredentials(creds);
      onConnected(creds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    } finally {
      setConnecting(false);
    }
  }, [serverUrl, code, onConnected]);

  // Auto-connect if both params provided
  useEffect(() => {
    if (serverUrl && code && !autoConnectRef.current) {
      autoConnectRef.current = true;
      connect();
    }
  }, [serverUrl, code, connect]);

  return (
    <div style={{
      background: '#000', color: '#fff', height: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ width: 400, padding: 32 }}>
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>HumanVu TV</h1>
        <p style={{ color: '#888', marginBottom: 24, fontSize: 14 }}>
          Enter the server URL and registration code from the dashboard.
        </p>

        <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#aaa' }}>Server URL</label>
        <input
          type="text"
          value={serverUrl}
          onChange={(e) => setServerUrl(e.target.value)}
          placeholder="https://humanvu-api.mndev.co.za/api"
          style={{
            width: '100%', padding: '10px 12px', marginBottom: 16, background: '#1a1a1a',
            border: '1px solid #333', borderRadius: 6, color: '#fff', fontSize: 14,
            boxSizing: 'border-box',
          }}
        />

        <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#aaa' }}>Registration Code</label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABCD1234"
          maxLength={8}
          style={{
            width: '100%', padding: '10px 12px', marginBottom: 24, background: '#1a1a1a',
            border: '1px solid #333', borderRadius: 6, color: '#fff', fontSize: 20,
            letterSpacing: 4, textAlign: 'center', fontFamily: 'monospace',
            boxSizing: 'border-box',
          }}
        />

        {error && (
          <p style={{ color: '#ef4444', marginBottom: 16, fontSize: 13 }}>{error}</p>
        )}

        <button
          onClick={connect}
          disabled={connecting || !serverUrl || !code}
          style={{
            width: '100%', padding: '12px', background: connecting ? '#333' : '#3b82f6',
            border: 'none', borderRadius: 6, color: '#fff', fontSize: 16, cursor: 'pointer',
            opacity: (!serverUrl || !code) ? 0.5 : 1,
          }}
        >
          {connecting ? 'Connecting...' : 'Connect'}
        </button>
      </div>
    </div>
  );
}

// ─── Player Screen ───────────────────────────────────────────

function PlayerScreen({ credentials }: { credentials: Credentials }) {
  const playlistVideoRef = useRef<HTMLVideoElement>(null);
  const triggerVideoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const durationTimerRef = useRef<number>(0);
  const [config, setConfig] = useState<MediaPlayerConfig | null>(null);
  const [status, setStatus] = useState('Loading config...');
  const [currentItemLabel, setCurrentItemLabel] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [isTriggered, setIsTriggered] = useState(false);
  const [triggerVideoName, setTriggerVideoName] = useState('');
  const [cacheReady, setCacheReady] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<string>('');
  const [activeItemType, setActiveItemType] = useState<string>('');
  const playlistIndexRef = useRef(0);
  const triggerQueueRef = useRef<{ videoId: string; videoName: string }[]>([]);
  const lastTriggerReceivedRef = useRef<number>(0);
  const hideControlsTimer = useRef<number>(0);
  const swRegistration = useRef<ServiceWorkerRegistration | null>(null);
  const { serverUrl, apiKey, playerId } = credentials;

  // Build the download URL for a video (goes through Service Worker cache)
  const getVideoUrl = useCallback((videoId: string): string => {
    return `${serverUrl}/videos/${videoId}/download`;
  }, [serverUrl]);

  // Register Service Worker on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.register('/tv-sw.js').then((reg) => {
      swRegistration.current = reg;
      // Send API key to SW so it can auth uncached video requests
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SET_API_KEY', apiKey });
      }
    }).catch((err) => {
      console.warn('Service Worker registration failed:', err);
    });

    // Listen for precache messages
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PRECACHE_COMPLETE') {
        setCacheReady(true);
        setCacheStatus(`${event.data.cached || 0} video(s) cached`);
      } else if (event.data?.type === 'PRECACHE_PROGRESS') {
        setCacheStatus(`Caching ${event.data.current}/${event.data.total}...`);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [apiKey]);

  // Pre-cache all videos (playlist videos + demographic trigger videos) when config loads
  useEffect(() => {
    if (!config) return;

    const videoIds = new Set<string>();
    // Playlist video items
    config.playlist.forEach(item => {
      if (item.item_type === 'video' && item.video) {
        videoIds.add(item.video.id);
      }
    });
    // Zone trigger videos
    config.zone_triggers.forEach(zt => videoIds.add(zt.video.id));
    // Demographic trigger videos
    config.demographic_videos.forEach(dv => videoIds.add(dv.video.id));

    if (videoIds.size === 0) {
      setCacheReady(true);
      return;
    }

    const urls = Array.from(videoIds).map(id => getVideoUrl(id));

    // Try Service Worker precache
    if (navigator.serviceWorker?.controller) {
      setStatus(`Caching ${videoIds.size} video(s)...`);
      navigator.serviceWorker.controller.postMessage({
        type: 'PRECACHE_VIDEOS',
        urls,
        headers: { 'X-API-Key': apiKey },
      });
    } else {
      // SW not yet active, mark ready to play (will cache on first fetch)
      setCacheReady(true);
    }
  }, [config, apiKey, getVideoUrl]);

  // Hide controls after inactivity
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = window.setTimeout(() => setShowControls(false), 3000);
  }, []);

  useEffect(() => {
    resetControlsTimer();
    const handler = () => resetControlsTimer();
    document.addEventListener('mousemove', handler);
    return () => {
      document.removeEventListener('mousemove', handler);
      clearTimeout(hideControlsTimer.current);
    };
  }, [resetControlsTimer]);

  // Load config on mount
  useEffect(() => {
    let cancelled = false;

    async function loadConfig() {
      try {
        const res = await apiFetch(serverUrl, `/media-players/${playerId}/config`, apiKey);
        const cfg: MediaPlayerConfig = await res.json();
        if (!cancelled) {
          setConfig(cfg);
          if (cfg.playlist.length === 0) setStatus('No content in playlist');
        }
      } catch (err) {
        if (!cancelled) setStatus(`Config error: ${err instanceof Error ? err.message : 'Unknown'}`);
      }
    }

    loadConfig();
    // Refresh config every 60 seconds
    const interval = setInterval(loadConfig, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [serverUrl, apiKey, playerId]);

  // Show a playlist item by index
  const showPlaylistItem = useCallback((index: number) => {
    if (!config || config.playlist.length === 0) return;

    const idx = index % config.playlist.length;
    const item = config.playlist[idx];
    playlistIndexRef.current = idx;

    // Clear any duration timer from previous item
    clearTimeout(durationTimerRef.current);

    if (item.item_type === 'video' && item.video) {
      setActiveItemType('video');
      setCurrentItemLabel(item.video.filename);
      setStatus('Playing');
      const videoEl = playlistVideoRef.current;
      if (videoEl) {
        videoEl.src = getVideoUrl(item.video.id);
        videoEl.play().catch((err) => {
          console.error('Failed to play video:', err);
          // Skip to next
          playlistIndexRef.current++;
          setTimeout(() => showPlaylistItem(playlistIndexRef.current), 1000);
        });
      }
    } else if (item.item_type === 'web_url' && item.url) {
      setActiveItemType('web_url');
      setCurrentItemLabel(item.url);
      setStatus('Showing webpage');
      // Pause any playing video
      playlistVideoRef.current?.pause();
      // Duration timer: advance after duration_seconds, or stay indefinitely
      if (item.duration_seconds && item.duration_seconds > 0) {
        durationTimerRef.current = window.setTimeout(() => {
          playlistIndexRef.current++;
          showPlaylistItem(playlistIndexRef.current);
        }, item.duration_seconds * 1000);
      }
    } else if (item.item_type === 'image' && item.url) {
      setActiveItemType('image');
      setCurrentItemLabel(item.url);
      setStatus('Showing image');
      // Pause any playing video
      playlistVideoRef.current?.pause();
      // Duration timer
      if (item.duration_seconds && item.duration_seconds > 0) {
        durationTimerRef.current = window.setTimeout(() => {
          playlistIndexRef.current++;
          showPlaylistItem(playlistIndexRef.current);
        }, item.duration_seconds * 1000);
      }
    } else {
      // Unknown type or missing data, skip
      playlistIndexRef.current++;
      setTimeout(() => showPlaylistItem(playlistIndexRef.current), 100);
    }
  }, [config, getVideoUrl]);

  // Start playlist when config loads and cache is ready
  useEffect(() => {
    if (config && config.playlist.length > 0 && cacheReady && !isTriggered) {
      showPlaylistItem(playlistIndexRef.current);
    }
  }, [config, cacheReady, showPlaylistItem, isTriggered]);

  // Handle playlist video ended -> advance to next
  useEffect(() => {
    const video = playlistVideoRef.current;
    if (!video) return;

    const onEnded = () => {
      if (!isTriggered) {
        playlistIndexRef.current++;
        showPlaylistItem(playlistIndexRef.current);
      }
    };
    video.addEventListener('ended', onEnded);
    return () => video.removeEventListener('ended', onEnded);
  }, [showPlaylistItem, isTriggered]);

  // Helper: signal trigger-done with retry
  const signalTriggerDone = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await apiFetch(serverUrl, `/media-players/${playerId}/trigger-done`, apiKey, { method: 'POST' });
        return;
      } catch (err) {
        console.error(`Failed to signal trigger-done (attempt ${attempt + 1}):`, err);
        if (attempt < 2) await new Promise(r => setTimeout(r, 2000));
      }
    }
  }, [serverUrl, playerId, apiKey]);

  // Helper: play next trigger from queue, or resume playlist if empty
  const playNextTriggerOrResume = useCallback(() => {
    const next = triggerQueueRef.current.shift();
    if (next && triggerVideoRef.current) {
      // Play next queued trigger immediately (no gap)
      triggerVideoRef.current.src = getVideoUrl(next.videoId);
      triggerVideoRef.current.play().then(() => {
        setTriggerVideoName(next.videoName);
        apiFetch(serverUrl, `/media-players/${playerId}/trigger-playing`, apiKey, { method: 'POST' })
          .catch(err => console.error('Failed to signal trigger-playing:', err));
      }).catch(() => {
        // Failed to play, try next or resume playlist
        playNextTriggerOrResume();
      });
    } else {
      // Queue empty, resume playlist
      setIsTriggered(false);
      setTriggerVideoName('');
      signalTriggerDone();
      showPlaylistItem(playlistIndexRef.current);
    }
  }, [signalTriggerDone, showPlaylistItem, getVideoUrl, serverUrl, playerId, apiKey]);

  // Handle trigger video ended or errored -> resume playlist
  useEffect(() => {
    const video = triggerVideoRef.current;
    if (!video) return;

    const onEnded = () => playNextTriggerOrResume();
    const onError = () => {
      console.error('Trigger video playback error');
      playNextTriggerOrResume();
    };
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
  }, [playNextTriggerOrResume]);

  // Poll for triggers (zone + demographic)
  useEffect(() => {
    const TRIGGER_IDLE_TIMEOUT_MS = 5000; // Clear queue if no new triggers for 5 seconds

    const interval = setInterval(async () => {
      try {
        const res = await apiFetch(serverUrl, `/media-players/${playerId}/triggers/pending`, apiKey);
        const triggers: PendingTrigger[] = await res.json();

        if (triggers.length > 0) {
          lastTriggerReceivedRef.current = Date.now();

          // Resolve video IDs and names for all triggers
          const resolved: { videoId: string; videoName: string }[] = [];
          for (const trigger of triggers) {
            let videoId = trigger.video_id;
            let videoName = 'Triggered video';

            if (!videoId && trigger.zone_id) {
              const zoneConfig = config?.zone_triggers.find(zt => zt.zone_id === trigger.zone_id);
              if (zoneConfig) {
                videoId = zoneConfig.video.id;
                videoName = zoneConfig.video.filename;
              }
            }

            if (videoId && trigger.trigger_type === 'demographic') {
              const demoConfig = config?.demographic_videos.find(dv => dv.video.id === videoId);
              if (demoConfig) videoName = demoConfig.video.filename;
            } else if (videoId) {
              const ztConfig = config?.zone_triggers.find(zt => zt.video.id === videoId);
              if (ztConfig) videoName = ztConfig.video.filename;
            }

            if (videoId) resolved.push({ videoId, videoName });
          }

          if (resolved.length === 0) return;

          const MAX_QUEUE = 5;

          if (isTriggered) {
            // Already playing a trigger - queue extras up to max
            const available = MAX_QUEUE - triggerQueueRef.current.length;
            if (available > 0) {
              triggerQueueRef.current.push(...resolved.slice(0, available));
            }
          } else {
            // Play first trigger immediately, queue the rest (up to max)
            const [first, ...rest] = resolved;
            triggerQueueRef.current.push(...rest.slice(0, MAX_QUEUE));

            // Pause playlist content
            playlistVideoRef.current?.pause();
            clearTimeout(durationTimerRef.current);

            triggerVideoRef.current!.src = getVideoUrl(first.videoId);
            try {
              await triggerVideoRef.current!.play();
              setIsTriggered(true);
              setTriggerVideoName(first.videoName);
              apiFetch(serverUrl, `/media-players/${playerId}/trigger-playing`, apiKey, { method: 'POST' })
                .catch(err => console.error('Failed to signal trigger-playing:', err));
            } catch (playErr) {
              console.error('Failed to play trigger video:', playErr);
              signalTriggerDone();
              showPlaylistItem(playlistIndexRef.current);
            }
          }
        } else if (isTriggered && lastTriggerReceivedRef.current > 0) {
          // No new triggers — check if idle timeout has passed
          const idleMs = Date.now() - lastTriggerReceivedRef.current;
          if (idleMs >= TRIGGER_IDLE_TIMEOUT_MS) {
            // Person likely left — clear queue and let current video finish
            // (don't interrupt mid-playback, just prevent more from playing)
            triggerQueueRef.current.length = 0;
          }
        }
      } catch (err) {
        console.error('Trigger poll error:', err);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [serverUrl, apiKey, playerId, config, isTriggered, getVideoUrl, signalTriggerDone, showPlaylistItem]);

  // Heartbeat
  useEffect(() => {
    const beat = () => {
      apiFetch(serverUrl, `/media-players/${playerId}/heartbeat`, apiKey, { method: 'POST' })
        .catch(console.error);
    };
    beat(); // Immediate
    const interval = setInterval(beat, 30000);
    return () => clearInterval(interval);
  }, [serverUrl, apiKey, playerId]);

  // Cleanup duration timer on unmount
  useEffect(() => {
    return () => clearTimeout(durationTimerRef.current);
  }, []);

  const disconnect = () => {
    clearCredentials();
    window.location.reload();
  };

  // Get current playlist item for rendering
  const currentItem = config?.playlist?.[playlistIndexRef.current % (config?.playlist?.length || 1)] || null;

  return (
    <div
      style={{
        background: '#000', color: '#fff', height: '100vh', width: '100vw',
        overflow: 'hidden', position: 'relative', cursor: showControls ? 'default' : 'none',
      }}
    >
      {/* Playlist video layer */}
      <video
        ref={playlistVideoRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
          display: (!isTriggered && activeItemType === 'video') ? 'block' : 'none',
        }}
        playsInline
        muted
        crossOrigin="anonymous"
        disableRemotePlayback
        disablePictureInPicture
      />

      {/* Playlist iframe layer (web_url) */}
      {activeItemType === 'web_url' && currentItem?.url && !isTriggered && (
        <iframe
          ref={iframeRef}
          src={currentItem.url}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            border: 'none',
          }}
          allow="autoplay; fullscreen"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      )}

      {/* Playlist image layer */}
      {activeItemType === 'image' && currentItem?.url && !isTriggered && (
        <img
          src={currentItem.url}
          alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'contain',
          }}
        />
      )}

      {/* Trigger video (overlay on top of everything) */}
      <video
        ref={triggerVideoRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
          display: isTriggered ? 'block' : 'none',
          zIndex: 10,
        }}
        playsInline
        muted
        crossOrigin="anonymous"
        disableRemotePlayback
        disablePictureInPicture
      />

      {/* Idle screen when no content */}
      {(!config || config.playlist.length === 0) && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexDirection: 'column',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>TV</div>
          <p style={{ color: '#666', fontSize: 18 }}>{status}</p>
          <p style={{ color: '#444', fontSize: 14, marginTop: 8 }}>
            Add content to the playlist in the dashboard
          </p>
        </div>
      )}

      {/* Status bar */}
      <div style={{
        position: 'absolute', bottom: 16, left: 16, zIndex: 20,
        background: 'rgba(0,0,0,0.7)', borderRadius: 8, padding: '8px 16px',
        fontSize: 12, color: '#888',
        opacity: showControls ? 1 : 0, transition: 'opacity 0.3s',
        pointerEvents: 'none',
      }}>
        {isTriggered && <span style={{ color: '#ef4444', marginRight: 8 }}>TRIGGERED</span>}
        {isTriggered && triggerVideoName && <span>{triggerVideoName}</span>}
        {!isTriggered && currentItemLabel && <span>{currentItemLabel}</span>}
        {!isTriggered && !currentItemLabel && <span>{status}</span>}
        {cacheStatus && <span style={{ marginLeft: 12, color: '#666' }}>[{cacheStatus}]</span>}
      </div>

      {/* Exit button */}
      <button
        onClick={disconnect}
        style={{
          position: 'absolute', top: 16, right: 16, zIndex: 20,
          background: 'rgba(0,0,0,0.7)', border: '1px solid #333',
          borderRadius: 8, padding: '8px 16px', color: '#888', fontSize: 12,
          cursor: 'pointer',
          opacity: showControls ? 1 : 0, transition: 'opacity 0.3s',
        }}
      >
        Disconnect
      </button>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────

export default function TvPlayer() {
  const [credentials, setCredentials] = useState<Credentials | null>(loadCredentials);

  if (!credentials) {
    return <SetupScreen onConnected={setCredentials} />;
  }

  return <PlayerScreen credentials={credentials} />;
}
